import { Component, Input, OnChanges, SimpleChanges } from '@angular/core'
import { Title } from '@angular/platform-browser'
import { ChangeRequest, ChangeRequestStatus } from '@app/core/model/change-request.model'
import { ApplicationSetting } from '@app/core/model/enums/application-setting.enum'
import { ScopeVersion } from '@app/core/model/scope-version'
import { AuthService } from '@app/core/service/auth.service'
import { User } from '@app/core/model/user.model'
import { ChangeRequestTab, ChangeRequestTabRoutes } from '../enum'
import { ActivatedRoute, Router } from '@angular/router'
import { ChangeRequestNavigationService } from '../service'
import { Privilege } from '@app/core/model/enums/privilege.enum'
import { MenuOptions } from '@app/core/model/definitions/menu-options.interface'
import { LanguageService } from '@app/core/service/language.service'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { Store } from '@ngrx/store'
import { plainToInstance } from 'class-transformer'
import { map, Observable } from 'rxjs'
import { AddDeliverableEvent } from '../../components/scope-overview/models'
import { CreateDeliverableModalComponent } from '../../components/scope-tab/create-deliverable-modal/create-deliverable-modal.component'
import { ScopeOverviewActions } from '../../store/actions/scope-overview.action'
import { ScopeOverviewSelectors } from '../../store/selectors/scope-overview.selector'
import { CdnConfig } from '@app/core/model/cdn-config.model'
import { FormControl } from '@angular/forms'
import { SelectedDeliverable, Deliverable } from '@app/features/scoping/models/deliverable.model'
import { Money } from '../../model/money.model'
import { ChangeRequestActions } from '../store/actions/change-request.actions'
import { getRandomInt } from '../util'
import { ScopeUiModalComponent } from '@shared/components/ui-components/scope-ui-modal/scope-ui-modal.component'
import { ModalConfig } from '@core/model/modal-config.model'
import { LibraryDeliverableService } from '@app/features/library-management/services'
import { FetchDeliverablePayload } from '../model'
import { ChangeRequestSelectors } from '../store/selectors/change-request.selectors'
import { ChangeRequestTrafficModal } from './change-request-traffic-modal/change-request-traffic-modal.component'
import { ChangeRequestIntegrationModalConfig } from '../model/change-request-integration-modal-config'
import { UserSettingsService } from '../../service/user-settings.service'
import { ScopeSection } from '../../model/scope-section'
import { EditDeliverablePayload } from '../model/edit-deliverable-payload.interface'
import { ChangeRequestDeliverableActions } from '../store/actions/change-request-deliverable.actions'
import { LibraryComponent } from '@app/features/library-management/store/models/component'
import { ScopeComponent } from '@app/features/scoping/models/component.model'
import { ScopeElementsService } from '@app/shared/components/scope-elements/services/scope-elements.service'
import { TableColumnPreferences } from '@app/shared/components/ui-components/scope-ui-table/table-column-preferences.type'
import { SCOPE_TAB_PARAM } from '../../service/scope-overview-navigation.service'
import { DeliverableSourceType } from '../../model/deliverable-type'

@Component({
  selector: 'change-request',
  templateUrl: './change-request.component.html',
  styleUrls: ['./change-request.component.scss'],
})
export class ChangeRequestComponent implements OnChanges {
  @Input() currentScope: ScopeVersion
  @Input() currentChangeRequest: ChangeRequest
  @Input() currentChangeRequestAbleToMerge: boolean
  @Input() changeRequests: ChangeRequest[]
  @Input() cdnConfig: CdnConfig

  @Input() public set filterPreferences(value: TableColumnPreferences | null) {
    if (value && this.scopeElementsService.userColumns) {
      Object.entries(value).forEach(
        ([key, value]) => (this.scopeElementsService.userColumns[key].selected = value.selected)
      )
      this.scopeElementsService.updateFilterTableColumns(this.currentScope)
      this.setColumns()
    }
  }

  isLoading$: Observable<boolean> = this.store$.select(ChangeRequestSelectors.selectAnyLoading)

  isEditable = false
  scopeActions!: MenuOptions[]
  mainColumnClass: string
  componentMainColumnClass: string
  roleMainColumnClass: string

  get loggedInUser(): User {
    return this.authService.loggedInUser
  }

  get currentTab(): string {
    return this.changeRequestNavigationService.currentTab
  }

  constructor(
    private titleService: Title,
    private dialog: MatDialog,
    private store$: Store,
    protected authService: AuthService,
    private libraryDeliverableService: LibraryDeliverableService,
    private lang: LanguageService,
    private route: ActivatedRoute,
    private router: Router,
    private changeRequestNavigationService: ChangeRequestNavigationService,
    private userSettingsService: UserSettingsService,
    private scopeElementsService: ScopeElementsService
  ) {
    this.fetchChangeRequest()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.currentChangeRequest && this.currentChangeRequest) {
      this.titleService.setTitle(this.currentChangeRequest.name + ' | Change Request | Scope')

      const isEditable =
        this.currentChangeRequestAbleToMerge && this.currentChangeRequest?.status === ChangeRequestStatus.DRAFT
      if (isEditable !== this.isEditable) this.isEditable = isEditable

      this.setMenu()
    }

    if (changes.currentChangeRequestAbleToMerge) this.checkAbleToMerge()

    if (changes.currentScope && this.currentScope) this.setColumns()
  }

  fetchDeliverable(payload: FetchDeliverablePayload) {
    this.store$.dispatch(ChangeRequestDeliverableActions.fetchDeliverable(payload))
  }

  editDeliverable(payload: EditDeliverablePayload) {
    this.router.navigateByUrl(
      `/scopes/scope-overview/${payload.scopeId}/change-request/${payload.changeRequestId}/deliverable/${payload.deliverableId}`
    )
  }

  checkAbleToMerge() {
    if (!this.currentChangeRequestAbleToMerge) {
      const modalConfig: ModalConfig = {
        title: 'Change Request has conflict',
        body: `This CR can cause merging conflict for other CRsTo avoid this please return to draft and resolve the conflicts. Alternatively, continue with merge and resolve the conflict in the affected CR.`,
        confirmText: 'Revert to draft for editing',
        cancelText: 'Close without merging',
        hasCancelButton: true,
        confirmCallback: () => {
          const comment = 'Moving to draft for editing conflict'
          if (this.currentChangeRequest.status == ChangeRequestStatus.CLIENT_APPROVED) {
            this.store$.dispatch(
              ChangeRequestActions.revertChangeRequestToDraft({
                scopeId: this.currentScope.identity.id,
                changeRequestId: this.currentChangeRequest.id,
                comment: comment,
              })
            )
          }
          dialog.close()
        },
        cancelCallback: () => {
          const comment = 'Closing Change Request because of conflict'
          if (this.currentChangeRequest.status == ChangeRequestStatus.CLIENT_APPROVED) {
            this.store$.dispatch(
              ChangeRequestActions.dismissChangeRequest({
                scopeId: this.currentScope.identity.id,
                changeRequestId: this.currentChangeRequest.id,
                comment: comment,
              })
            )
          }
          dialog.close()
        },
      }
      const dialog = this.dialog.open(ScopeUiModalComponent, { data: modalConfig })
    }
  }

  addDeliverable(deliverable: Deliverable) {
    this.store$.dispatch(ChangeRequestActions.addDeliverable({ data: deliverable }))
  }

  setDefaultTab(tab: string) {
    const scopeTab = this.router.routerState.snapshot.root.queryParams[SCOPE_TAB_PARAM]
    this.changeRequestNavigationService.selectTab(tab, !scopeTab)
  }

  selectTab(tab: string) {
    this.changeRequestNavigationService.selectTab(tab)
  }

  createDeliverable(isFixedFee = false) {
    this.createDeliverableDialog = this.dialog.open(CreateDeliverableModalComponent)
    this.store$.dispatch(ScopeOverviewActions.setDialogId({ id: this.createDeliverableDialog.id }))

    this.createDeliverableDialog.componentInstance.cdnConfig = this.cdnConfig!
    this.createDeliverableDialog.componentInstance.isFixedFee = isFixedFee
    this.createDeliverableDialog.componentInstance.currentScope = this.currentScope
    this.createDeliverableDialog.componentInstance.owner = this.loggedInUser
    this.createDeliverableDialog.componentInstance.currencyCode = this.currentScope.identity.rateCard.currencyCode
    this.createDeliverableDialog.componentInstance.disableCreateCustomDeliverable = true
    this.createDeliverableDialog.componentInstance.disableSetupBeforeAdding = true

    this.createDeliverableDialog.componentInstance.availableDeliverables$ = this.store$.select(
      ScopeOverviewSelectors.selectAvailableDeliverableTypes(
        this.currentScope.identity.rateCard.currencyCode,
        DeliverableSourceType.LIBRARY
      )
    )
    this.createDeliverableDialog.componentInstance.availableOwners$ = this.store$
      .select(ScopeOverviewSelectors.selectCurrentScope)
      .pipe(map((scope) => scope.identity.team.collaborators.map((c) => c.user)))

    this.createDeliverableDialog.componentInstance.confirmCallback.subscribe((event: AddDeliverableEvent) => {
      this.createDeliverableDialog.componentInstance.isRateCardsLoading = true
      this.createDeliverableDialog.close()

      event.selection.deliverableType.sourceType === DeliverableSourceType.SCOPE
        ? this.addExistingScopeDeliverable(
            event.form,
            event.selection,
            this.currentScope.identity.rateCard.currencyCode
          )
        : this.addExistingLibraryDeliverable(
            event.form,
            event.selection,
            this.currentScope.identity.rateCard.currencyCode
          )
    })

    this.createDeliverableDialog.componentInstance.onSearchDeliverables.subscribe((searchText: string) => {
      if (isFixedFee) {
        this.store$.dispatch(
          ScopeOverviewActions.deliverableTypeSearch({
            fixedOnly: true,
            searchQuery: searchText,
            rateCardVersionId: this.currentScope.identity.rateCard.id,
            language: this.currentScope.identity.language,
          })
        )
      } else if (searchText && searchText.length) {
        this.store$.dispatch(
          ScopeOverviewActions.deliverableTypeSearch({
            searchQuery: searchText,
            rateCardVersionId: this.currentScope.identity.rateCard.id,
            language: this.currentScope.identity.language,
          })
        )
      } else {
        this.store$.dispatch(
          ScopeOverviewActions.getAvailableScopeDeliverableTypes({ mergeLibraryDeliverableEntryStrategy: 'DISCIPLINE' })
        )
      }
    })
  }

  deleteDeliverable(deliverable: Deliverable) {
    this.deleteItem(deliverable, () => {
      this.store$.dispatch(
        ChangeRequestActions.deleteDeliverable({
          deliverableId: deliverable.changeAuditData?.identity || deliverable.id,
        })
      )
    })
  }

  deleteComponent(component: ScopeComponent) {
    this.deleteItem(component, () => {
      this.store$.dispatch(
        ChangeRequestActions.deleteComponent({
          componentId: component.changeAuditData?.identity || component.id,
          deliverableId: component.deliverable.changeAuditData?.identity || component.deliverable.id,
        })
      )
    })
  }

  openTrafficModal() {
    if (!this.currentChangeRequest) return

    this.userSettingsService.getCompanyIntegrations().subscribe((integrations: any[]) => {
      const dialogConfig: ChangeRequestIntegrationModalConfig = {
        title: 'Change request Traffic',
        body: 'Select to which systems you want to send your Change request data',
        integrations: integrations,
        currentScope: this.currentScope,
        currentUser: this.loggedInUser,
        limitModalWidth: true,
        scopeId: this.currentScope.identity.id,
        changeRequestId: this.currentChangeRequest!.id,
      }
      this.dialog.open(ChangeRequestTrafficModal, { data: dialogConfig })
    })
  }

  addExistingScopeDeliverable(form: FormControl, selection: SelectedDeliverable, currencyUnit: string) {
    const deliverableDraft = this.getDeliverablePayload(form, selection, currencyUnit)

    this.resetCreateDeliverableDialogValues()
    this.addDeliverable(deliverableDraft)
  }

  addExistingLibraryDeliverable(form: FormControl, selection: SelectedDeliverable, currencyUnit: string) {
    this.libraryDeliverableService.getDeliverableEntry(selection.deliverableType.id).subscribe((deliverable) => {
      const deliverableDraft = plainToInstance(Deliverable, {
        ...deliverable,
        ...this.getDeliverablePayload(form, selection, currencyUnit),
        libraryComponents: deliverable.libraryComponents?.map((libraryComponent) =>
          plainToInstance(LibraryComponent, {
            ...libraryComponent,
            id: getRandomInt(),
          })
        ),
      })

      this.resetCreateDeliverableDialogValues()
      this.addDeliverable(deliverableDraft)
    })
  }

  getDeliverablePayload(form: FormControl, selection: SelectedDeliverable, currencyUnit: string) {
    const { name, budget } = form.value
    return plainToInstance(Deliverable, {
      id: getRandomInt(),
      source: selection.deliverableType,
      sourceType: selection.deliverableType.sourceType,
      owner: this.loggedInUser as User,
      name: name ? name : selection.deliverableType.name,
      description: selection.description || selection.deliverableType.description,
      budget: new Money(budget || 0, currencyUnit),
      startDate: selection.startDate,
      endDate: selection.endDate,
      discipline: selection.deliverableType.discipline,
    })
  }

  resetCreateDeliverableDialogValues() {
    if (this.createDeliverableDialog?.componentInstance)
      this.createDeliverableDialog.componentInstance.isRateCardsLoading = false
  }

  createSection(): void {
    const dialog = this.dialog.open(ScopeUiModalComponent, {
      data: {
        title: `Add new ${this.lang.get('stage')}`,
        confirmText: 'Complete',
        confirmCallback: (form: FormControl) => {
          dialog.close()
          const { name, description, duration } = form.value
          const section = plainToInstance(ScopeSection, {
            name,
            description,
            startDate: duration?.startDate,
            endDate: duration?.endDate,
          })
          this.store$.dispatch(
            ChangeRequestActions.addSection({ data: plainToInstance(ScopeSection, { ...section, id: getRandomInt() }) })
          )
        },
        isFormValid: (form: FormControl) => form.value.name,
        inputs: [
          { name: 'name', control: new FormControl(''), type: 'text', label: 'Name' },
          { name: 'description', control: new FormControl(''), type: 'description' },
          {
            name: 'duration',
            control: new FormControl({ startDate: this.currentScope.startDate, endDate: this.currentScope.endDate }),
            type: 'duration',
            minDate: this.currentScope.startDate,
            maxDate: this.currentScope.endDate,
          },
        ],
        isLoading$: this.store$.select(ScopeOverviewSelectors.selectAddScopeSectionLoading),
      },
    })
  }

  setColumns() {
    if (!this.scopeElementsService.userColumns) this.scopeElementsService.setUserColumns(this.currentScope)
    this.scopeElementsService.setFeeColumns()
    this.scopeElementsService.setThirdPartyCostColumns()
    this.setColumnClasses()
  }

  private setColumnClasses() {
    this.mainColumnClass = this.scopeElementsService.calculateMainColumnClass()
    this.componentMainColumnClass = this.scopeElementsService.calculateComponentMainColumnClass()
    this.roleMainColumnClass = this.scopeElementsService.calculateRoleMainColumnClass()
  }

  private fetchChangeRequest() {
    this.route.paramMap.subscribe((params) => {
      const changeRequestId = Number(params.get('changeRequestId'))

      if (changeRequestId && this.currentChangeRequest?.id !== changeRequestId)
        this.store$.dispatch(ChangeRequestActions.getChangeRequest({ changeRequestId }))
    })
  }

  private deleteItem(item: Deliverable | ScopeComponent, callback: () => void) {
    const dialog = this.dialog.open(ScopeUiModalComponent, {
      data: {
        title: `Delete "` + item.name + `"`,
        body: `Are you sure you want to delete "` + item.name + `"?`,
        confirmText: 'Delete',
        confirmCallback: () => {
          dialog.close()
          callback()
        },
        cancelCallback: () => dialog.close(),
        flatButton: true,
        limitBodyWidth: true,
      },
    })
  }

  private setMenu(): void {
    this.scopeActions = [
      {
        name: () => this.lang.get('deliverable'),
        icon: () => 'description',
        callback: () => this.createDeliverable(),
      },
      { name: () => 'Fixed Fee', icon: () => 'request_page', callback: () => this.createDeliverable(true) },
      { name: () => this.lang.get('stage'), icon: () => 'flag', callback: () => this.createSection() },
    ]
  }

  protected readonly ApplicationSetting = ApplicationSetting
  protected readonly ChangeRequestTab = ChangeRequestTab
  protected readonly ChangeRequestTabRoutes = ChangeRequestTabRoutes
  protected readonly Privilege = Privilege

  // TODO: Refactor
  private createDeliverableDialog: MatDialogRef<CreateDeliverableModalComponent, any>
}
